import {useState, type FormEvent} from 'react';
import {AwAppShell, AwButton, AwErrorState, AwField, AwLoadingState, AwModal, AwPageHeader} from '@ascension-tracker/ascension-ui';
import {api} from '../api.js';
import {useAsync} from '../hooks/useAsync.js';
import {useCreateTask, useEnsureDefaultSections, useProjectSections} from '../hooks/useTasks.js';
import {ListView} from './project-views/ListView.js';
import {BoardView} from './project-views/BoardView.js';
import {TimelineView} from './project-views/TimelineView.js';
import {CalendarView} from './project-views/CalendarView.js';
import {TaskDetailPanel} from '../components/TaskDetailPanel.js';

type ProjectSummary = {id: string; projectKey: string; displayName: string};
type View = 'list' | 'board' | 'timeline' | 'calendar';
const views: Array<{id: View; label: string}> = [
  {id: 'list', label: 'List'},
  {id: 'board', label: 'Board'},
  {id: 'timeline', label: 'Timeline'},
  {id: 'calendar', label: 'Calendar'}
];

export function useProjectIdForKey(projectKey: string) {
  const projects = useAsync(() => api<{items: ProjectSummary[]}>('/projects'), []);
  return {projectId: projects.data?.items.find(project => project.projectKey === projectKey)?.id, error: projects.error};
}

export function ProjectPage({projectKey}: {projectKey: string}) {
  const {projectId, error} = useProjectIdForKey(projectKey);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [view, setView] = useState<View>('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const sections = useProjectSections(projectId ?? '');
  const createTask = useCreateTask(projectId ?? '');
  useEnsureDefaultSections(projectId ?? '', sections.data);
  if (error) return <AwAppShell projectKey={projectKey}><AwErrorState>{error}</AwErrorState></AwAppShell>;
  if (!projectId) return <AwAppShell projectKey={projectKey}><AwLoadingState/></AwAppShell>;

  const defaultSection = sections.data?.find(section => section.isDefault) ?? null;

  async function submitCreateTask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await createTask.mutateAsync({title: String(form.get('title')), sectionId: defaultSection?.id ?? null});
    setCreatingTask(false);
  }

  return (
    <AwAppShell
      projectKey={projectKey}
      quickActions={<>
        <AwButton type="button" onClick={() => setCreatingTask(true)}>Create Task</AwButton>
        <AwButton type="button" variant="secondary" disabled title="Bulk editing is not yet available">Bulk Edit</AwButton>
      </>}
    >
      <AwPageHeader
        eyebrow={`Project: ${projectKey}`}
        title="Tasks"
        actions={
          <div className="task-page-actions">
            <div className="task-view-switch" role="tablist" aria-label="Task view">
              {views.map(option => (
                <AwButton key={option.id} type="button" variant={view === option.id ? 'primary' : 'secondary'} aria-pressed={view === option.id} onClick={() => setView(option.id)}>{option.label}</AwButton>
              ))}
            </div>
            {view === 'list' && <AwButton type="button" variant="secondary" aria-pressed={filtersOpen} onClick={() => setFiltersOpen(open => !open)}>Filter</AwButton>}
            <AwButton type="button" variant="secondary" disabled title="Bulk editing is not yet available">Bulk Edit</AwButton>
            <AwButton type="button" onClick={() => setCreatingTask(true)}>Create Task</AwButton>
          </div>
        }
      >
        <p>Organise work into sections, assign owners, and track due dates.</p>
      </AwPageHeader>
      {view === 'list' && <ListView projectId={projectId} projectKey={projectKey} onOpenTask={setSelectedTaskId} filtersOpen={filtersOpen}/>}
      {view === 'board' && <BoardView projectId={projectId} onOpenTask={setSelectedTaskId}/>}
      {view === 'timeline' && <TimelineView projectId={projectId} projectKey={projectKey} onOpenTask={setSelectedTaskId}/>}
      {view === 'calendar' && <CalendarView projectId={projectId} onOpenTask={setSelectedTaskId}/>}
      <TaskDetailPanel taskId={selectedTaskId} onClose={() => setSelectedTaskId(null)}/>
      <AwModal open={creatingTask} title="Create Task" onClose={() => setCreatingTask(false)}>
        <form className="project-form" onSubmit={submitCreateTask}>
          <AwField id="create-task-title" name="title" label="Task Title" required autoFocus/>
          <div className="project-form__actions">
            <AwButton type="button" variant="secondary" onClick={() => setCreatingTask(false)}>Cancel</AwButton>
            <AwButton type="submit">Create Task</AwButton>
          </div>
        </form>
      </AwModal>
    </AwAppShell>
  );
}
